/**
 * ChatPanel — the scrollable conversation surface for the AI assistant.
 *
 * Responsibilities:
 *  - Maintains message history (user + assistant bubbles).
 *  - Calls streamChat() from the aiAssistant service (SSE, through the api instance).
 *  - Maps every AgentEvent type to the correct UI update:
 *      token       → appends text to the in-progress assistant bubble
 *      tool_call   → shows the RunningHorse thinking indicator with tool name
 *      tool_result → keeps the thinking indicator running (more tokens follow)
 *      data        → attaches a DataPayload to the in-progress bubble
 *      done        → hides indicator, persists conversationId for multi-turn
 *      error       → hides indicator, appends an error alert bubble
 *  - Pre-stream failures (streamChat throws StreamHttpError):
 *      SESSION_EXPIRED → nothing shown: the api interceptor raised the password
 *                        dialog, and the question goes out again after it (INV-49)
 *      401   → re-auth nudge
 *      other → the server's own message (a 403 says which refusal it is)
 *  - Online guard: disables input and shows OFFLINE_HELPER_TEXT when !isOnline.
 *  - Cleans up: AbortController on unmount / new send before old stream finishes.
 *
 * Does NOT: touch FAB expand/collapse, voice recording, Snackbar — those stay
 * in the parent (index.tsx).
 */

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
  useContext,
  useId,
} from 'react';
import {
  Box,
  TextField,
  IconButton,
  Alert,
  Typography,
  Chip,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';
import VoiceCommandButton from '../VoiceCommandButton';
import { RunningHorse } from './RunningHorse';
import { ASSISTANT_NAME, OFFLINE_HELPER_TEXT } from './ReinsightMark';
import ChatMessage, { ChatMessageData, DataPayload } from './ChatMessage';
import { streamChat, confirmAction, cancelAction, rateMessage, AgentEvent, StreamHttpError } from '../../services/aiAssistant';
import { useOffline } from '../../offline';
import { useRBAC } from '../../auth/useRBAC';
import { AuthContext } from '../../auth/AuthContext';
import { isSessionExpiredError } from '../../auth/sessionPolicy';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------



/** Tappable starter prompts shown in the empty state so new users know what to ask. */
export const STARTER_SUGGESTIONS = [
  'What can you do?',
  'How many appointments do I have today?',
  'Which invoices are unpaid?',
  'How do I record a vaccination?',
] as const;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function makeId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function preStreamErrorMessage(err: StreamHttpError): string {
  if (err.status === 401) {
    return 'Your session has expired. Please refresh the page and sign in again.';
  }
  return err.message || `Request failed (HTTP ${err.status}). Please try again.`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface ChatPanelProps {
  /** Called to pass isStreaming state up so the parent can disable the FAB
   *  collapse during active streaming if desired. Optional. */
  onStreamingChange?: (isStreaming: boolean) => void;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ onStreamingChange }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { isOnline } = useOffline();
  const { hasPermission, permissionsPending } = useRBAC();
  const { reauthCount } = useContext(AuthContext);

  // Gate mirror. The four ai-assistant handlers require 'view:medical_records'
  // since the P3 sweep re-pointed them off 'view:all'. The panel already asked for
  // the destination slug in P2, so nothing moved here — only this comment's
  // premise did. Every role holds the slug, so no real user's access changed.
  // ⚠ While claims are unresolved this is false and the input IS disabled
  // (see isInputDisabled below) — the pre-P2 comment claimed otherwise.
  const hasAccess = hasPermission('view:medical_records');

  // ── Conversation state ──
  const [messages, setMessages] = useState<ChatMessageData[]>([]);
  const [conversationId, setConversationId] = useState<string | undefined>();
  const [isStreaming, setIsStreaming] = useState(false);
  const [thinking, setThinking] = useState<string | null>(null); // tool name or null
  const [input, setInput] = useState('');

  // Inline error for pre-stream failures (quota/auth)
  const [preStreamError, setPreStreamError] = useState<string | null>(null);

  // AbortController for the current SSE stream
  const abortRef = useRef<AbortController | null>(null);
  // Tracks the message id of the in-progress assistant bubble
  const streamingMsgIdRef = useRef<string | null>(null);
  // A question the 24-hour password dialog cut off: sent again once the password is accepted
  const heldRef = useRef<string | null>(null);
  const seenReauthsRef = useRef(reauthCount);

  // Scroll anchor
  const bottomRef = useRef<HTMLDivElement>(null);

  // ── Propagate streaming state ──
  useEffect(() => {
    onStreamingChange?.(isStreaming);
  }, [isStreaming, onStreamingChange]);

  // ── Auto-scroll to bottom when messages update ──
  useEffect(() => {
    // scrollIntoView is not available in jsdom; guard for test environments.
    if (bottomRef.current && typeof bottomRef.current.scrollIntoView === 'function') {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, thinking]);

  // ── Abort on unmount ──
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
    };
  }, []);

  // ── Handle a single AgentEvent ──
  // IMPORTANT: read streamingMsgIdRef.current IMMEDIATELY at call time (not
  // inside a lazy state updater) so the value is captured before the `done`
  // event can null the ref. React may batch the resulting `setMessages` calls
  // and execute them after subsequent synchronous events have already cleared
  // the ref.
  const handleEvent = useCallback((event: AgentEvent) => {
    switch (event.type) {
      case 'token': {
        // Capture msgId at call time, before any later event can clear the ref.
        const msgId = streamingMsgIdRef.current;
        if (!msgId) break;
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, text: m.text + event.text } : m,
          ),
        );
        // The answer is now streaming in — drop the thinking indicator.
        setThinking(null);
        break;
      }

      case 'tool_call': {
        setThinking(event.name);
        break;
      }

      case 'tool_result': {
        // Thinking indicator stays on; tool result means more work is coming.
        break;
      }

      case 'data': {
        // Capture msgId at call time.
        const msgId = streamingMsgIdRef.current;
        if (!msgId) break;
        const payload: DataPayload = { columns: event.columns, rows: event.rows };
        setMessages((prev) =>
          prev.map((m) =>
            m.id === msgId ? { ...m, data: payload } : m,
          ),
        );
        break;
      }

      case 'done': {
        setConversationId(event.conversationId);
        // Attach the server-side message id to the answer we just streamed so
        // it can be rated. Older backends omit assistantMessageIds — the thumbs
        // simply don't render then.
        const answerId = event.assistantMessageIds?.[event.assistantMessageIds.length - 1];
        const streamedId = streamingMsgIdRef.current;
        if (answerId && streamedId) {
          setMessages((prev) =>
            prev.map((m) => (m.id === streamedId ? { ...m, messageId: answerId } : m)),
          );
        }
        setThinking(null);
        setIsStreaming(false);
        streamingMsgIdRef.current = null;
        abortRef.current = null;
        break;
      }

      case 'confirm_required': {
        // Push a new assistant message carrying the confirm payload (status: pending).
        // The streaming bubble (if any) stays; this card is its own separate message.
        const confirmMsgId = makeId();
        setMessages((prev) => [
          ...prev,
          {
            id: confirmMsgId,
            role: 'assistant' as const,
            text: '',
            confirm: {
              actionId: event.actionId,
              toolName: event.toolName,
              title: event.title,
              summary: event.summary,
              fields: event.fields,
              status: 'pending' as const,
            },
          },
        ]);
        // We're waiting on the user — hide the thinking indicator.
        setThinking(null);
        break;
      }

      case 'error': {
        // Capture msgId at call time before clearing.
        const msgId = streamingMsgIdRef.current;
        setMessages((prev) => {
          if (msgId) {
            const inProgress = prev.find((m) => m.id === msgId);
            if (inProgress && inProgress.text === '') {
              return prev.map((m) =>
                m.id === msgId
                  ? { ...m, text: event.message, isError: true }
                  : m,
              );
            }
          }
          // Otherwise append a separate error bubble.
          return [
            ...prev,
            {
              id: makeId(),
              role: 'assistant' as const,
              text: event.message,
              isError: true,
            },
          ];
        });
        setThinking(null);
        setIsStreaming(false);
        streamingMsgIdRef.current = null;
        abortRef.current = null;
        break;
      }

      default:
        break;
    }
  }, []);

  // ── Confirm / cancel a pending write action ──

  const handleConfirm = useCallback(async (actionId: string) => {
    try {
      const result = await confirmAction(actionId);
      setMessages((prev) =>
        prev.map((m) =>
          m.confirm?.actionId === actionId
            ? { ...m, confirm: { ...m.confirm!, status: 'confirmed' as const, resultMessage: result.message } }
            : m,
        ),
      );
    } catch (err) {
      // The password dialog is up; the card keeps waiting for the same click (INV-49).
      if (isSessionExpiredError(err)) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.confirm?.actionId === actionId
            ? { ...m, confirm: { ...m.confirm!, status: 'error' as const, resultMessage: 'Something went wrong. Please try again.' } }
            : m,
        ),
      );
    }
  }, []);

  /**
   * Rate an answer. Optimistic: the thumbs are replaced by a thank-you
   * immediately, and a failed request silently reverts — a feedback control
   * that throws an error dialog at the user is worse than one that no-ops.
   */
  const handleRate = useCallback(async (messageId: string, rating: 1 | -1) => {
    setMessages((prev) =>
      prev.map((m) => (m.messageId === messageId ? { ...m, rating } : m)),
    );
    try {
      await rateMessage(messageId, rating);
    } catch {
      setMessages((prev) =>
        prev.map((m) => (m.messageId === messageId ? { ...m, rating: undefined } : m)),
      );
    }
  }, []);

  const handleCancelAction = useCallback(async (actionId: string) => {
    try {
      await cancelAction(actionId);
      setMessages((prev) =>
        prev.map((m) =>
          m.confirm?.actionId === actionId
            ? { ...m, confirm: { ...m.confirm!, status: 'cancelled' as const } }
            : m,
        ),
      );
    } catch (err) {
      if (isSessionExpiredError(err)) return;
      setMessages((prev) =>
        prev.map((m) =>
          m.confirm?.actionId === actionId
            ? { ...m, confirm: { ...m.confirm!, status: 'error' as const, resultMessage: 'Something went wrong. Please try again.' } }
            : m,
        ),
      );
    }
  }, []);

  // ── Send a message ──
  // `resend` is a held question going out again: its bubble is already in the thread.
  const sendMessage = useCallback(
    async (text: string, resend = false) => {
      const trimmed = text.trim();
      if (!trimmed || isStreaming || !isOnline) return;

      // Clear any previous pre-stream error
      setPreStreamError(null);

      // Abort any lingering prior stream
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      if (!resend) {
        // Push user bubble
        setMessages((prev) => [
          ...prev,
          { id: makeId(), role: 'user', text: trimmed },
        ]);
        setInput('');
      }

      // Create a blank assistant bubble to stream into
      const assistantMsgId = makeId();
      streamingMsgIdRef.current = assistantMsgId;
      setMessages((prev) => [
        ...prev,
        { id: assistantMsgId, role: 'assistant', text: '' },
      ]);

      setIsStreaming(true);
      setThinking(''); // show the running horse immediately on send ("Thinking…")

      try {
        await streamChat(
          { message: trimmed, conversationId },
          { onEvent: handleEvent },
          { signal: controller.signal },
        );
      } catch (err: unknown) {
        // We aborted it (a new message, or the panel unmounted): ignore. Checked on the signal
        // because axios reports an abort before the response as a CanceledError.
        if (controller.signal.aborted) {
          // Remove the blank assistant bubble
          setMessages((prev) =>
            prev.filter((m) => m.id !== assistantMsgId),
          );
          setIsStreaming(false);
          streamingMsgIdRef.current = null;
          abortRef.current = null;
          return;
        }

        if (isSessionExpiredError(err)) {
          // The api interceptor raised the 24-hour password dialog, so this is not an answer
          // (INV-49): the question waits and goes out again once the password is accepted.
          heldRef.current = trimmed;
        } else {
          // Show as a pre-stream banner (not in the thread — distinct from mid-stream errors)
          setPreStreamError(preStreamErrorMessage(err as StreamHttpError));
        }

        // Remove the blank assistant placeholder
        setMessages((prev) =>
          prev.filter((m) => m.id !== assistantMsgId),
        );

        setThinking(null);
        setIsStreaming(false);
        streamingMsgIdRef.current = null;
        abortRef.current = null;
      }
    },
    [isStreaming, isOnline, conversationId, handleEvent],
  );

  // ── The password was accepted: send the held question, once ──
  useEffect(() => {
    if (reauthCount === seenReauthsRef.current) return;
    seenReauthsRef.current = reauthCount;
    const held = heldRef.current;
    heldRef.current = null;
    if (held !== null) void sendMessage(held, true);
  }, [reauthCount, sendMessage]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleVoiceCommand = (transcript: string) => {
    sendMessage(transcript);
  };

  const isInputDisabled = isStreaming || !isOnline || !hasAccess;
  // Say WHY the composer is dead. Offline first (the stronger statement), then the
  // permission read still in flight: P2 stretched that window from a token refresh
  // to a token refresh plus an HTTP round trip (plus its retry), and "Ask …" on a
  // disabled input reads as a broken assistant rather than a pending one.
  const composerPlaceholder = !isOnline
    ? 'Offline — unavailable'
    : permissionsPending
      ? 'Checking your access…'
      : `Ask ${ASSISTANT_NAME}…`;

  // ── Render ──
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        // Taller panel to give the chat room to breathe.
        height: { xs: 420, sm: 500 },
        overflow: 'hidden',
      }}
    >
      {/* Offline notice */}
      {!isOnline && (
        <Alert severity="info" sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}>
          {OFFLINE_HELPER_TEXT}
        </Alert>
      )}

      {/* Pre-stream error (quota / auth) */}
      {preStreamError && (
        <Alert
          severity="warning"
          onClose={() => setPreStreamError(null)}
          sx={{ mb: 1, py: 0.5, fontSize: '0.75rem' }}
        >
          {preStreamError}
        </Alert>
      )}

      {/* Message list */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          overflowX: 'hidden',
          pr: 0.5,
          // Prevent long words/URLs from ever causing horizontal overflow
          wordBreak: 'break-word',
          overflowWrap: 'anywhere',
          // Custom thin scrollbar
          '&::-webkit-scrollbar': { width: 4 },
          '&::-webkit-scrollbar-thumb': { backgroundColor: 'divider', borderRadius: 4 },
        }}
        role="log"
        aria-label="Chat messages"
        aria-live="polite"
      >
        {messages.length === 0 && (
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: 'column',
              gap: 1.5,
              px: 1,
            }}
          >
            <Typography
              variant="body2"
              color="text.secondary"
              align="center"
              sx={{ opacity: 0.8 }}
            >
              Ask me about appointments, patients, invoices, or anything about your clinic.
            </Typography>
            {!isInputDisabled && (
              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  justifyContent: 'center',
                  gap: 0.75,
                }}
              >
                {STARTER_SUGGESTIONS.map((suggestion) => (
                  <Chip
                    key={suggestion}
                    label={suggestion}
                    size="small"
                    variant="outlined"
                    clickable
                    onClick={() => sendMessage(suggestion)}
                    sx={{
                      fontSize: '0.75rem',
                      borderColor: 'primary.main',
                      color: 'primary.main',
                      '&:hover': { bgcolor: 'action.hover' },
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}

        {messages
          // Hide the blank assistant placeholder while the AI is thinking — the
          // running-horse indicator stands in for it. The bubble appears the
          // moment real content arrives (first token, data, an error, or a
          // confirm card).
          .filter((msg) => msg.role === 'user' || msg.text !== '' || msg.isError || !!msg.data || !!msg.confirm)
          .map((msg) => (
            <ChatMessage
              key={msg.id}
              message={msg}
              onConfirm={handleConfirm}
              onCancel={handleCancelAction}
              onRate={handleRate}
            />
          ))}

        {/* Thinking indicator — shows from send, through tool calls, until the
            answer text starts streaming (thinking is set to null on the first token). */}
        {isStreaming && thinking !== null && (
          <Box sx={{ pl: 1, pb: 1 }}>
            <RunningHorse
              size={28}
              label={
                thinking
                  ? `Looking up ${thinking.replace(/_/g, ' ')}…`
                  : 'Thinking…'
              }
            />
          </Box>
        )}

        {/* Scroll anchor */}
        <div ref={bottomRef} />
      </Box>

      {/* Conversation context chip */}
      {conversationId && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, py: 0.5 }}>
          <Chip
            label="Continuing conversation"
            size="small"
            variant="outlined"
            onDelete={() => {
              setConversationId(undefined);
              setMessages([]);
              heldRef.current = null;
            }}
            deleteIcon={
              <Box
                component="span"
                sx={{ fontSize: '0.7rem', px: 0.25, lineHeight: 1 }}
              >
                ×
              </Box>
            }
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Box>
      )}

      {/* Input area */}
      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          display: 'flex',
          gap: { xs: 0.5, sm: 0.75 },
          alignItems: 'center',
          pt: 0.75,
          borderTop: '1px solid',
          borderColor: 'divider',
          mt: 0.5,
        }}
      >
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          placeholder={composerPlaceholder}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          disabled={isInputDisabled}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              sendMessage(input);
            }
          }}
          sx={{
            '& .MuiOutlinedInput-root': { borderRadius: 2 },
            '& .MuiOutlinedInput-input': {
              py: { xs: 0.875, sm: 1 },
              px: { xs: 1.25, sm: 1.5 },
              fontSize: { xs: '0.8125rem', sm: '0.875rem' },
            },
          }}
        />
        <VoiceCommandButton
          onVoiceCommand={handleVoiceCommand}
          isProcessing={isStreaming}
          disabled={isInputDisabled}
          size={isMobile ? 'small' : 'medium'}
        />
        <IconButton
          type="submit"
          color="primary"
          disabled={isInputDisabled || !input.trim()}
          aria-label="Send message"
          sx={{
            bgcolor: 'primary.main',
            color: '#FAF9F6',
            width: { xs: 36, sm: 40 },
            height: { xs: 36, sm: 40 },
            flexShrink: 0,
            '&:hover': { bgcolor: 'primary.dark' },
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' },
          }}
        >
          <SendIcon fontSize="small" />
        </IconButton>
      </Box>
    </Box>
  );
};

export default ChatPanel;
